import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Icon } from "@iconify/react";
import swal from "sweetalert2";
import { Button, PageTitle } from "../StyledElements";
import ModelFormDialog from "./ModelFormDialog";
import ModelChatDialog from "./ModelChatDialog";
import { deleteModel, getModels, testModel, updateModel } from "../../services/models";
import {
  PROVIDER_LABELS,
  canEditModel,
  describeTestResult,
  isClientModel,
  isGlobalModel,
  isVisibleToClient,
} from "../../utils/modelRegistry";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  color: var(--main-font-color);
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const Card = styled.div`
  border: 1px solid var(--content-item-border-color);
  background-color: var(--card-bg);
  border-radius: 8px;
  padding: 16px;
  box-shadow: var(--content-item-box-shadow);
`;

const CardTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const Muted = styled.div`
  font-size: 13px;
  font-style: italic;
  color: #a0a6b1;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 8px;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #a0a6b1;
  border-bottom: 1px solid var(--content-item-border-color);
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px 8px;
  border-bottom: 1px solid var(--content-item-border-color);
  vertical-align: top;
`;

const Pill = styled.span`
  display: inline-block;
  padding: 2px 9px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 999px;
  color: var(--button-font-color);
  background-color: ${(props) => (props.$tone === "on" ? "#2e7d32" : props.$tone === "global" ? "#4a5568" : "#8a8f99")};
`;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
  flex-wrap: wrap;
`;

const Small = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 9px;
  font-size: 13px;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid var(--main-border-color);
  color: var(--main-font-color);

  &:hover {
    background-color: var(--content-item-bg);
  }
`;

const ErrorBanner = styled.div`
  padding: 10px 12px;
  font-size: 14px;
  border-radius: 6px;
  border: 1px solid #c0392b;
  background-color: rgba(192, 57, 43, 0.1);
  color: #c0392b;
`;

const Truncated = styled.span`
  display: inline-block;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
`;

const describeDefault = (entry) => {
  if (!entry) return "not configured";
  const provider = PROVIDER_LABELS[entry.provider] ?? entry.provider ?? "unknown";
  return [provider, entry.deployment, entry.api_version, entry.api_key_masked]
    .filter(Boolean)
    .join(" · ");
};

/**
 * Model registry management, used at both levels.
 *
 * - `scope="admin"`: every model, admin-level and per-client; anything can be changed.
 * - `scope="client"`: only the given client's models plus the admin-level (global) ones, and
 *   only the client's own are editable.
 */
export const ModelsManager = ({ scope = "admin", clientId = null, clients = [] }) => {
  const isClientScope = scope === "client";

  const [models, setModels] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [chatModel, setChatModel] = useState(null);
  const [editingModel, setEditingModel] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchModels = async () => {
    setError(null);

    // A client view must never fall back to the unfiltered list, which would expose every
    // client's models.
    if (isClientScope && !clientId) {
      setModels([]);
      setLoading(false);
      setError("No client is selected, so there are no client models to show.");
      return;
    }

    setLoading(true);

    // With a client id the API returns that client's models plus the globals; without one the
    // admin console gets everything.
    const result = await getModels(isClientScope ? { clientId } : {});

    if (!result.success) {
      setError(result.message || "Could not load the model registry.");
      setModels([]);
    } else {
      const visible = isClientScope
        ? result.models.filter((model) => isVisibleToClient(model, clientId))
        : result.models;
      setModels(visible);
      setDefaults(result.defaults ?? {});
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, clientId]);

  const clientName = (model) => {
    if (isGlobalModel(model)) return null;
    const match = clients.find((client) => String(client.public_id ?? client.id) === String(model.client_id));
    return match?.name ?? String(model.client_id);
  };

  const editable = (model) => canEditModel(model, { scope, clientId });

  const openCreate = () => {
    setEditingModel(null);
    setShowDialog(true);
  };

  const openEdit = (model) => {
    setEditingModel(model);
    setShowDialog(true);
  };

  const runTest = async (model) => {
    const idOrName = model.public_id ?? model.name;
    setBusyId(idOrName);
    const result = await testModel(idOrName);
    setBusyId(null);

    swal.fire({
      title: result.success ? "Connection OK" : "Test failed",
      text: describeTestResult(result),
      icon: result.success ? "success" : "error",
    });
  };

  const toggleActive = async (model) => {
    const idOrName = model.public_id ?? model.name;
    setBusyId(idOrName);
    const result = await updateModel(idOrName, { is_active: !model.is_active });
    setBusyId(null);

    if (!result.success) {
      swal.fire({ title: "Error", text: result.message || "Could not update the model.", icon: "error" });
      return;
    }

    fetchModels();
  };

  const confirmDelete = async (model) => {
    const idOrName = model.public_id ?? model.name;

    const confirmation = await swal.fire({
      title: `Delete "${model.name}"?`,
      text: "Anything referencing it falls back to the environment defaults. To disable instead, use Deactivate.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    });

    if (!confirmation.isConfirmed) return;

    setBusyId(idOrName);
    const result = await deleteModel(idOrName);
    setBusyId(null);

    if (!result.success) {
      swal.fire({ title: "Error", text: result.message || "Could not delete the model.", icon: "error" });
      return;
    }

    fetchModels();
  };

  return (
    <Wrapper>
      <Header>
        <PageTitle title="Models" style={{ marginBottom: 0 }} />
        <div style={{ flex: 1 }} />
        <Button onClick={openCreate}>{isClientScope ? "+ Add client model" : "+ Register model"}</Button>
      </Header>

      <Muted>
        {isClientScope
          ? "Your own model endpoints are editable. Admin-level models are provided for everyone and are read-only."
          : "Admin-level models are visible to every client; client models belong to one client. Reference a model by name in the workflow builder or generator."}
      </Muted>

      {!isClientScope && (
        <Card>
          <CardTitle>Environment fallback</CardTitle>
          <div style={{ fontSize: "14px" }}>Primary: {describeDefault(defaults?.primary)}</div>
          {defaults?.secondary ? (
            <div style={{ fontSize: "14px", marginTop: "4px" }}>
              Secondary: {describeDefault(defaults.secondary)}
            </div>
          ) : null}
          <Muted style={{ marginTop: "8px" }}>Read-only — set on the server, not editable here.</Muted>
        </Card>
      )}

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <Card>
        {loading ? (
          <Muted>Loading models…</Muted>
        ) : models.length === 0 ? (
          <Muted>
            {isClientScope
              ? "No models available yet. Admin-level models will appear here once registered."
              : "No models registered yet. Everything falls back to the environment defaults."}
          </Muted>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>{isClientScope ? "Scope" : "Client"}</Th>
                <Th>Provider</Th>
                <Th>Deployment</Th>
                <Th>Endpoint</Th>
                <Th>Key</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {models.map((model) => {
                const idOrName = model.public_id ?? model.name;
                const busy = busyId === idOrName;
                const mayEdit = editable(model);
                const own = isClientModel(model, clientId);
                const label = clientName(model);

                return (
                  <tr key={idOrName}>
                    <Td style={{ fontWeight: 600 }}>{model.name}</Td>
                    <Td>
                      {label ? label : <Pill $tone="global">Admin-level</Pill>}
                      {isClientScope && own && <Muted>your client</Muted>}
                    </Td>
                    <Td>{PROVIDER_LABELS[model.provider] ?? model.provider ?? "—"}</Td>
                    <Td>{model.deployment || "—"}</Td>
                    <Td title={model.endpoint ?? ""}>
                      <Truncated>{model.endpoint || "—"}</Truncated>
                      {model.api_version ? <Muted>{model.api_version}</Muted> : null}
                    </Td>
                    <Td>{model.api_key_masked ?? <Muted>not set</Muted>}</Td>
                    <Td>
                      <Pill $tone={model.is_active !== false ? "on" : "off"}>
                        {model.is_active !== false ? "Active" : "Inactive"}
                      </Pill>
                    </Td>
                    <Td>
                      <Actions>
                        <Small onClick={() => runTest(model)} title="Send a tiny completion">
                          <Icon icon="mdi:plug" /> {busy ? "…" : "Test"}
                        </Small>
                        <Small onClick={() => setChatModel(model)} title="Chat with this model to verify it works">
                          <Icon icon="mdi:message-text-outline" /> Chat
                        </Small>
                        {mayEdit && (
                          <>
                            <Small onClick={() => openEdit(model)}>
                              <Icon icon="mdi:pencil" /> Edit
                            </Small>
                            <Small onClick={() => toggleActive(model)}>
                              <Icon icon={model.is_active !== false ? "mdi:toggle-switch" : "mdi:toggle-switch-off-outline"} />
                              {model.is_active !== false ? "Deactivate" : "Activate"}
                            </Small>
                            <Small onClick={() => confirmDelete(model)} title="Delete permanently">
                              <Icon icon="mdi:trash-can-outline" /> Delete
                            </Small>
                          </>
                        )}
                        {!mayEdit && <Muted style={{ alignSelf: "center" }}>read-only</Muted>}
                      </Actions>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      <ModelChatDialog
        show={Boolean(chatModel)}
        model={chatModel}
        onClose={() => setChatModel(null)}
      />

      <ModelFormDialog
        show={showDialog}
        model={editingModel}
        clients={clients}
        scope={scope}
        clientId={clientId}
        onClose={() => setShowDialog(false)}
        onSaved={() => {
          setShowDialog(false);
          fetchModels();
        }}
      />
    </Wrapper>
  );
};

export default ModelsManager;
